"use client";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useQueryParams } from "../../../../utils/QueryParamsProvider/QueryParamsProvider.js";
import { ModuleSettingsStore } from "../../../../utils/contentstack/moduleSettingsStore.js";
import { useState, useEffect, useMemo } from "react";
import { Skeleton, Carousel } from "@nxt-costco-com/forge-components";
import { Analytics } from "../../../Analytics/index.js";
import { AdSetTitle } from "../../AdSet/AdSetTitle.js";
import { skeletonStyle } from "../Criteo/styles.js";
import { CATEGORY_ITEM_TYPE, AD_BUTLER_NO_ADS_STATUS } from "../constants.js";
import { isRotatingAd, fetchAdButlerData } from "../utils.js";
import { useCheckScreen } from "../../../../hooks/useCheckScreen/index.js";
import { AdButlerAd } from "./AdButlerAd.js";
import { AdButlerBannerComposer } from "./AdButlerBannerComposer.js";
import { AdButlerProductTileCarousel } from "./AdButlerProductTileCarousel.js";
import { getAdButlerComponentType } from "./adButlerUtils.js";
import { VIEW_NAME_SKELETON } from "./constants.js";
const AdButlerClient = ({
  adButlerConfig,
  adData,
  entry,
  titleProps,
  locale,
  isSearchPage,
  moduleSettings = {},
  externalLinkModule,
  maxSlidesBySize
}) => {
  var _a, _b;
  const { queryParams: searchParams } = useQueryParams();
  const searchKeyword = isSearchPage ? searchParams == null ? void 0 : searchParams.get("keyword") : void 0;
  const [adPlacementData, setAdPlacementData] = useState(adData);
  const [clientDataLoaded, setClientDataLoaded] = useState(false);
  const { isMobile, isTablet } = useCheckScreen();
  const {
    ad_set_style,
    enable_url_tracking,
    item_type,
    rotating_options
    // title
  } = entry;
  const { enable_autoplay = false, randomize_items } = rotating_options;
  useEffect(() => {
    const fetchAdSetData = async () => {
      var _a2;
      const { third_party_curation } = entry || {};
      const { adbutler_extension } = third_party_curation;
      const { siteId, kw } = adButlerConfig;
      if (!isSearchPage || searchKeyword) {
        try {
          let adPreviewHash = null;
          if (typeof document !== "undefined" && ((_a2 = document == null ? void 0 : document.location) == null ? void 0 : _a2.search)) {
            const params = new URLSearchParams(
              document.location.search
            );
            adPreviewHash = params.get("_adb_lwp");
          }
          const adButlerApiResponse = await fetchAdButlerData(
            siteId,
            adbutler_extension,
            adPreviewHash,
            searchKeyword || kw
          );
          const { placements, status } = adButlerApiResponse;
          if (status !== AD_BUTLER_NO_ADS_STATUS) {
            setAdPlacementData(placements);
          } else {
            setAdPlacementData(null);
          }
          setClientDataLoaded(true);
        } catch {
          console.error(
            `Error occured in fetching ad butler ads on client for siteId: ${siteId} and zoneId: ${adbutler_extension}`
          );
        }
      }
    };
    fetchAdSetData();
  }, [adButlerConfig, entry, searchKeyword, isSearchPage]);
  const msStore = new ModuleSettingsStore({
    key_value: moduleSettings == null ? void 0 : moduleSettings.key_value
  });
  const { template_params, unit_size, body } = (adPlacementData == null ? void 0 : adPlacementData[0]) || {};
  const adButlerProducts = useMemo(() => {
    var _a2;
    if (body) {
      try {
        const parsedBody = JSON.parse(
          body
        );
        if ((_a2 = parsedBody == null ? void 0 : parsedBody.products) == null ? void 0 : _a2.length) {
          return parsedBody.products.map(
            ({ product_id }) => product_id
          );
        }
      } catch (err) {
        console.error(
          `Error parsing ad butler product carousel body: ${err}`
        );
      }
    }
    return [];
  }, [body]);
  if (!adPlacementData || adPlacementData.length === 0) {
    return null;
  }
  const isRotating = isRotatingAd(ad_set_style);
  const titleId = `AdButler_${(_b = (_a = titleProps == null ? void 0 : titleProps.title) == null ? void 0 : _a.trim) == null ? void 0 : _b.call(_a).replace(/\s/g, "-")}`;
  if (isRotating && item_type === CATEGORY_ITEM_TYPE) {
    return null;
  }
  const { ad_type = "large_banner" } = template_params || {};
  const ComponentType = getAdButlerComponentType(
    ad_type,
    unit_size,
    item_type
  );
  const isProductTileCarousel = isRotating && (adButlerProducts == null ? void 0 : adButlerProducts.length) > 0;
  if (!clientDataLoaded) {
    return /* @__PURE__ */ jsx(Skeleton, { "data-testid": VIEW_NAME_SKELETON, sx: skeletonStyle });
  }
  const keyValue = msStore.key_value;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    titleProps && !isProductTileCarousel && /* @__PURE__ */ jsx(
      AdSetTitle,
      {
        titleWrapperSx: isProductTileCarousel ? { width: "100%" } : {},
        titleId,
        ...titleProps
      }
    ),
    /* @__PURE__ */ jsxs(
      Analytics,
      {
        analyticData: {
          adItem: {
            style: isRotating ? "carousel" : "banner"
          },
          doNotTrack: enable_url_tracking == null ? void 0 : enable_url_tracking.disable_third_party_tracking,
          adPlacementData: adPlacementData[0]
        },
        children: [
          isProductTileCarousel && /* @__PURE__ */ jsx(
            AdButlerAd,
            {
              AdComponent: /* @__PURE__ */ jsx(
                AdButlerProductTileCarousel,
                {
                  componentType: ComponentType,
                  entry,
                  itemNumbers: adButlerProducts,
                  locale,
                  titleProps,
                  maxSlidesBySize
                }
              ),
              adData: adPlacementData == null ? void 0 : adPlacementData[0],
              clientDataLoaded,
              itemType: "product_carousel"
            }
          ),
          isRotating && !isProductTileCarousel && /* @__PURE__ */ jsx(AdButlerBannerComposer, { componentType: ComponentType, children: /* @__PURE__ */ jsx(
            Carousel,
            {
              containerSx: {
                margin: 0,
                padding: 0
              },
              contentTypeJoinder: keyValue.contenttypejoinder,
              contentTypeLabel: keyValue.contenttypelabel,
              disableBottomControls: (adPlacementData == null ? void 0 : adPlacementData.length) === 1,
              items: adPlacementData == null ? void 0 : adPlacementData.map((ad, index) => /* @__PURE__ */ jsx(
                AdButlerAd,
                {
                  adData: ad,
                  clientDataLoaded,
                  externalLinkModule,
                  itemType: item_type
                },
                index
              )),
              navigationButtonsBottomAlign: Boolean(
                isMobile || isTablet
              ),
              navigationButtonsMobileDisplay: Boolean(
                isMobile || isTablet
              ),
              slideContainerSx: {
                marginBottom: "20px"
              },
              disableControls: (adPlacementData == null ? void 0 : adPlacementData.length) === 1,
              isAutoplay: enable_autoplay,
              itemsPerSlide: 1,
              label: keyValue.label,
              labelledBy: (titleProps == null ? void 0 : titleProps.title) ? titleId : void 0,
              nextLabel: keyValue.nextlabel,
              pauseLabel: keyValue.pauselabel,
              playLabel: keyValue.playlabel,
              previousLabel: keyValue.previouslabel,
              randomizeItems: randomize_items,
              uniqueId: "adset-thirdparty"
            }
          ) }),
          !isRotating && /* @__PURE__ */ jsx(AdButlerBannerComposer, { componentType: ComponentType, children: adPlacementData == null ? void 0 : adPlacementData.map((ad, index) => /* @__PURE__ */ jsx(
            AdButlerAd,
            {
              adData: ad,
              clientDataLoaded,
              externalLinkModule,
              itemType: item_type
            },
            index
          )) })
        ]
      }
    )
  ] });
};
export {
  AdButlerClient
};
